import {
  assignCaptionEmphasis,
  buildCaptionEvents,
  type CaptionEvent,
  type CaptionPagingOptions,
  type CaptionWord,
} from "./clip-captions.logic";
import { buildCropXExpression, type FramingPlan, type FramingTrack } from "./clip-framing.logic";
import { type ElevenLabsTranscriptWord } from "./elevenlabs-transcription";

/**
 * Pure planning logic for the clip renderer (TRE-1979 / TRE-1980): turns a candidate's
 * word-level transcript slice into cut segments and burned-in captions.
 *
 * Everything here is string/number crunching with no I/O so it can be unit-tested; the
 * ffmpeg invocation itself lives in `clip-renderer.ts`.
 */

export interface ClipWord {
  text: string;
  startMs: number;
  endMs: number;
  speakerId: string | null;
}

/** Keeps only real words with usable timing; times move from seconds to integer ms. */
export const normalizeTranscriptWords = (words: ElevenLabsTranscriptWord[]): ClipWord[] =>
  words
    .filter((word) => word.type === "word" && word.start != null && word.end != null && word.text.trim().length > 0)
    .map((word) => ({
      text: word.text.trim(),
      startMs: Math.round((word.start ?? 0) * 1000),
      endMs: Math.round((word.end ?? 0) * 1000),
      speakerId: word.speaker_id ?? null,
    }));

const SENTENCE_END_RE = /[.!?…][)"'”»]*$/;

const isSentenceEnd = (word: ClipWord) => SENTENCE_END_RE.test(word.text);

/**
 * Polish prefaces that precede the actual hook ("no więc", "dobra", "słuchajcie"…). A clip
 * that opens on them wastes its first second (etap D: the first word must be the hook).
 */
const PREFACE_PHRASES = [
  ["no", "więc"],
  ["no", "i"],
  ["no", "to"],
  ["no", "dobra"],
  ["no", "dobrze"],
  ["a", "więc"],
  ["tak", "więc"],
  ["z", "całym", "tym"],
  ["wiesz", "co"],
  ["wiecie", "co"],
  ["dobra"],
  ["dobrze"],
  ["okej"],
  ["ok"],
  ["słuchajcie"],
  ["słuchaj"],
  ["więc"],
  ["czyli"],
  ["generalnie"],
  ["właśnie"],
  ["yyy"],
  ["eee"],
];
const bareWord = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

/**
 * Transition sentences ("Idziemy dalej, bo drugi punkt…") that bridge from the previous
 * thought. When the clip opens on one and it is short, the hook is the next sentence.
 */
const TRANSITION_PHRASES = [
  ["idziemy", "dalej"],
  ["przejdźmy", "dalej"],
  ["lecimy", "dalej"],
  ["przechodzimy", "dalej"],
  ["kolejny", "punkt"],
  ["kolejna", "rzecz"],
  ["następny", "punkt"],
  ["następna", "rzecz"],
  ["drugi", "punkt"],
  ["trzeci", "punkt"],
  ["punkt", "numer"],
];
const TRANSITION_MAX_MS = 5_000;

const startsWithPhrase = (sentence: ClipWord[], phrases: string[][]) =>
  phrases.some(
    (phrase) =>
      sentence.length >= phrase.length && phrase.every((word, index) => bareWord(sentence[index]!.text) === word),
  );

/**
 * Moves the start past a leading preface when the rest of the sentence still carries at
 * least `minWordsLeft` words. Returns how many ms were cut so the render can report it.
 */
export const stripPreface = (
  words: ClipWord[],
  startMs: number,
  { minWordsLeft = 3 }: { minWordsLeft?: number } = {},
): { startMs: number; cutMs: number } => {
  const firstIndex = words.findIndex((word) => word.startMs >= startMs);
  if (firstIndex < 0) return { startMs, cutMs: 0 };
  let sentenceEnd = firstIndex;
  while (sentenceEnd < words.length - 1 && !isSentenceEnd(words[sentenceEnd]!)) sentenceEnd += 1;
  const sentence = words.slice(firstIndex, sentenceEnd + 1);
  const nextSentence = words[sentenceEnd + 1];
  if (
    nextSentence &&
    startsWithPhrase(sentence, TRANSITION_PHRASES) &&
    sentence[sentence.length - 1]!.endMs - sentence[0]!.startMs <= TRANSITION_MAX_MS
  ) {
    return { startMs: nextSentence.startMs, cutMs: nextSentence.startMs - startMs };
  }
  let cursor = 0;
  let matched = true;
  while (matched) {
    matched = false;
    for (const phrase of PREFACE_PHRASES) {
      const slice = sentence.slice(cursor, cursor + phrase.length).map((word) => bareWord(word.text));
      if (slice.length === phrase.length && slice.every((word, index) => word === phrase[index])) {
        cursor += phrase.length;
        matched = true;
        break;
      }
    }
  }
  if (cursor === 0 || sentence.length - cursor < minWordsLeft) return { startMs, cutMs: 0 };
  const newStart = sentence[cursor]!.startMs;
  return { startMs: newStart, cutMs: newStart - startMs };
};

/**
 * "No false endings": when a short closing sentence (a button — imperative, principle,
 * punchline) starts within `windowMs` after the chosen end, include it. Long sentences are
 * left out: they would be a new thought, not a landing.
 */
export const extendToButton = (
  words: ClipWord[],
  endMs: number,
  { windowMs = 3_000, maxButtonWords = 7 }: { windowMs?: number; maxButtonWords?: number } = {},
): { endMs: number; addedMs: number } => {
  const nextIndex = words.findIndex((word) => word.startMs >= endMs);
  if (nextIndex < 0 || words[nextIndex]!.startMs - endMs > windowMs) return { endMs, addedMs: 0 };
  let end = nextIndex;
  while (end < words.length - 1 && !isSentenceEnd(words[end]!)) end += 1;
  if (!isSentenceEnd(words[end]!)) return { endMs, addedMs: 0 };
  const button = words.slice(nextIndex, end + 1);
  if (button.length > maxButtonWords) return { endMs, addedMs: 0 };
  return { endMs: button[button.length - 1]!.endMs, addedMs: button[button.length - 1]!.endMs - endMs };
};

/** Sentences as the editor shows them: in/out points can only land on these boundaries. */
export const splitIntoSentences = (words: ClipWord[]): ClipWord[][] => {
  const sentences: ClipWord[][] = [];
  let current: ClipWord[] = [];
  for (const word of words) {
    current.push(word);
    if (isSentenceEnd(word)) {
      sentences.push(current);
      current = [];
    }
  }
  if (current.length) sentences.push(current);
  return sentences;
};

/**
 * The agent returns rough timings; the clip must start right after the previous sentence
 * and end exactly on a sentence boundary — a clip cut mid-word fails QC gate 4. The snap
 * never moves a boundary by more than `maxShiftMs` so a transcript with sparse punctuation
 * cannot silently double the clip.
 */
export const snapToSentences = (
  words: ClipWord[],
  startMs: number,
  endMs: number,
  { maxShiftMs = 7_000 }: { maxShiftMs?: number } = {},
): { startMs: number; endMs: number } => {
  if (!words.length) throw new Error("No timed words for sentence boundaries");

  const sentenceStarts = words
    .filter((_, index) => index === 0 || isSentenceEnd(words[index - 1]!))
    .map((word) => word.startMs);
  const sentenceEnds = words.filter(isSentenceEnd).map((word) => word.endMs);
  const snappedStart = nearestBoundary(sentenceStarts, startMs, maxShiftMs);
  const snappedEnd = nearestBoundary(
    sentenceEnds.filter((boundary) => boundary > snappedStart),
    endMs,
    maxShiftMs,
  );

  if (snappedEnd <= snappedStart) {
    throw new Error("No complete sentence within the clip window");
  }

  return { startMs: snappedStart, endMs: snappedEnd };
};

const nearestBoundary = (boundaries: number[], requestedMs: number, maxShiftMs: number): number => {
  let nearest: number | null = null;
  let bestDistance = maxShiftMs + 1;
  for (const boundary of boundaries) {
    const distance = Math.abs(boundary - requestedMs);
    if (distance <= maxShiftMs && distance < bestDistance) {
      nearest = boundary;
      bestDistance = distance;
    }
  }
  if (nearest == null) throw new Error(`No sentence boundary within ${maxShiftMs}ms of ${requestedMs}ms`);
  return nearest;
};

export interface RenderSegment {
  fromMs: number;
  toMs: number;
}

/** Silence trimming uses timed speech. Framing preserves the complete source image:
 * diarization identifies voices, not their positions in the picture. */
export const buildRenderSegments = (
  words: ClipWord[],
  startMs: number,
  endMs: number,
  { maxGapMs = 900, padMs = 120, guardMs = 40 }: { maxGapMs?: number; padMs?: number; guardMs?: number } = {},
): RenderSegment[] => {
  const clipWords = words.filter((word) => word.endMs > startMs && word.startMs < endMs);
  const first = clipWords[0];
  const last = clipWords[clipWords.length - 1];
  // The range ends on word boundaries, so the clip edges get the same breathing room as
  // every other cut — but never into the neighbouring word (timings jitter by tens of ms).
  const previous = first ? words[words.indexOf(first) - 1] : undefined;
  const next = last ? words[words.indexOf(last) + 1] : undefined;
  const headMs = clampMs(first && previous ? first.startMs - previous.endMs - guardMs : padMs, padMs);
  const tailMs = clampMs(last && next ? next.startMs - last.endMs - guardMs : padMs, padMs);
  const rangeStart = Math.max(0, startMs - headMs);
  const rangeEnd = endMs + tailMs;
  const segments: RenderSegment[] = [];
  for (const word of clipWords) {
    const fromMs = Math.max(rangeStart, word.startMs - padMs);
    const toMs = Math.min(rangeEnd, word.endMs + padMs);
    const previous = segments[segments.length - 1];
    if (previous && word.startMs - (previous.toMs - padMs) <= maxGapMs) {
      previous.toMs = Math.max(previous.toMs, toMs);
    } else if (toMs > fromMs) {
      segments.push({ fromMs, toMs });
    }
  }
  return segments;
};

const clampMs = (value: number, max: number) => Math.max(0, Math.min(max, value));

/** How much silence the output keeps after the last spoken word — the room an end fade has. */
export const trailingSilenceMs = (words: ClipWord[], segments: RenderSegment[]): number => {
  const last = segments[segments.length - 1];
  if (!last) return 0;
  const lastWordEnd = Math.max(
    ...words.filter((word) => word.startMs >= last.fromMs && word.startMs < last.toMs).map((word) => word.endMs),
  );
  return Number.isFinite(lastWordEnd) ? Math.max(0, last.toMs - lastWordEnd) : 0;
};

/** Maps absolute source time to the concatenated output timeline; null inside a trimmed gap. */
export const buildTimelineMapper = (segments: RenderSegment[]) => {
  const offsets: { fromMs: number; toMs: number; outputStartMs: number }[] = [];
  let cursor = 0;
  for (const segment of segments) {
    offsets.push({ fromMs: segment.fromMs, toMs: segment.toMs, outputStartMs: cursor });
    cursor += segment.toMs - segment.fromMs;
  }

  return (sourceMs: number): number | null => {
    for (const offset of offsets) {
      if (sourceMs >= offset.fromMs && sourceMs <= offset.toMs) {
        return offset.outputStartMs + (sourceMs - offset.fromMs);
      }
    }
    return null;
  };
};

export const totalOutputDurationMs = (segments: RenderSegment[]) =>
  segments.reduce((sum, segment) => sum + (segment.toMs - segment.fromMs), 0);

export * from "./clip-captions.logic";

export interface ClipCaptionEdit {
  /** Start of the corrected word in the SOURCE recording — stable across window changes. */
  wordStartMs: number;
  text: string;
}

/**
 * Human caption corrections from the Studio editor (etap F1). A word is matched by its
 * source start time, so the same edit survives moving the in/out points. An empty text
 * drops the word from the captions (the audio still plays it).
 */
export const applyCaptionEdits = (words: ClipWord[], edits: ClipCaptionEdit[] = []): ClipWord[] => {
  if (!edits.length) return words;
  const byStart = new Map(edits.map((edit) => [edit.wordStartMs, edit.text.trim()]));
  return words.flatMap((word) => {
    const text = byStart.get(word.startMs);
    if (text === undefined) return [word];
    return text ? [{ ...word, text }] : [];
  });
};

export interface ClipCaptionPlan {
  startMs: number;
  endMs: number;
  segments: RenderSegment[];
  captionWords: CaptionWord[];
  events: CaptionEvent[];
  durationMs: number;
  /** Silence kept after the last word; the render fades over it and never over the word. */
  endFadeMs: number;
  /** Hook-first adjustments (etap D) applied to the first and the last range. */
  hook: { firstWordMs: number; prefaceCutMs: number; buttonAddedMs: number };
}

/**
 * Everything the render and the in-panel preview share: sentence snap, silence-trimmed
 * segments, words mapped onto the output timeline, caption pages. Pure, so the browser
 * preview and the server render cannot disagree about where a cut or a page falls.
 */
export interface ClipRange {
  startMs: number;
  endMs: number;
}

export interface ClipCaptionPlanOptions extends CaptionPagingOptions {
  maxCharsPerLine: number;
  maxLines: number;
  captionEdits?: ClipCaptionEdit[];
  /** Mark strong / payoff words (etap B); off keeps every word at base weight. */
  emphasis?: boolean;
  /** Open on the hook word and land on a closing sentence (etap D); off keeps the raw snap. */
  hookFirst?: boolean;
}

/**
 * Plan for a clip stitched from several source ranges in the given order (etap G: an ad is
 * hook → value → cta, each from a different minute of the recording). Every range snaps to
 * sentences on its own; the output timeline is the concatenation, so captions of the value
 * block start right where the hook's end.
 */
export const planClipCaptionsForRanges = (
  words: ClipWord[],
  ranges: ClipRange[],
  { captionEdits, emphasis = true, hookFirst = true, ...paging }: ClipCaptionPlanOptions,
): ClipCaptionPlan => {
  const snappedRanges = ranges.map((range) => snapToSentences(words, range.startMs, range.endMs));
  let prefaceCutMs = 0;
  let buttonAddedMs = 0;
  if (hookFirst && snappedRanges.length) {
    const first = snappedRanges[0]!;
    const strippedStart = stripPreface(words, first.startMs);
    if (strippedStart.startMs < first.endMs) {
      first.startMs = strippedStart.startMs;
      prefaceCutMs = strippedStart.cutMs;
    }
    const last = snappedRanges[snappedRanges.length - 1]!;
    const extended = extendToButton(words, last.endMs);
    last.endMs = extended.endMs;
    buttonAddedMs = extended.addedMs;
  }
  const segments = snappedRanges.flatMap((range) => buildRenderSegments(words, range.startMs, range.endMs));
  const toOutputMs = buildTimelineMapper(segments);
  const captionSource = applyCaptionEdits(words, captionEdits);
  const captionWords: CaptionWord[] = [];
  for (const range of snappedRanges) {
    for (const word of captionSource) {
      if (word.endMs <= range.startMs || word.startMs >= range.endMs) continue;
      const fromMs = toOutputMs(word.startMs);
      const toMs = toOutputMs(word.endMs);
      if (fromMs == null || toMs == null || toMs <= fromMs) continue;
      captionWords.push({ text: word.text, fromMs, toMs });
    }
  }
  captionWords.sort((a, b) => a.fromMs - b.fromMs);
  const styled = emphasis ? assignCaptionEmphasis(captionWords) : captionWords;

  return {
    startMs: snappedRanges[0]?.startMs ?? 0,
    endMs: snappedRanges[snappedRanges.length - 1]?.endMs ?? 0,
    segments,
    captionWords: styled,
    events: buildCaptionEvents(styled, paging),
    durationMs: totalOutputDurationMs(segments),
    endFadeMs: Math.min(END_FADE_MS, trailingSilenceMs(words, segments)),
    hook: { firstWordMs: styled[0]?.fromMs ?? 0, prefaceCutMs, buttonAddedMs },
  };
};

export const planClipCaptions = (
  words: ClipWord[],
  startMs: number,
  endMs: number,
  options: ClipCaptionPlanOptions,
): ClipCaptionPlan => planClipCaptionsForRanges(words, [{ startMs, endMs }], options);

export interface ClipFilterGraph {
  filterComplex: string;
  videoLabel: string;
  audioLabel: string;
}

const msToSeconds = (ms: number) => (ms / 1000).toFixed(3);

/**
 * trim/atrim + concat + scale + padding as one filter graph producing the caption-less base
 * clip (captions are a separate pass: Remotion, or the ffmpeg/libass fallback). All segment
 * times are absolute source ms; `inputOffsetMs` is subtracted when the caller pre-seeks the
 * input (`-ss` before `-i`) so a clip at minute 25 does not decode 25 minutes of video.
 */
/**
 * Voice chain of the playbook (etap C1), as one ffmpeg filter string: spectral denoise,
 * rumble/hiss roll-off, a little presence, gentle compression, a brick-wall limiter, then
 * loudnorm to the platform target (-14 LUFS integrated, -1.5 dBTP). Single-pass loudnorm is
 * dynamic, which is fine for speech; the render measures the result (renderMeta.audio).
 */
const END_FADE_MS = 150;

export const buildLoudnorm = ({ targetLufs = -14, truePeakDb = -1.5 } = {}) =>
  `loudnorm=I=${targetLufs}:TP=${truePeakDb}:LRA=11`;

export const buildVoiceChain = ({
  targetLufs = -14,
  truePeakDb = -1.5,
  withLoudnorm = true,
}: { targetLufs?: number; truePeakDb?: number; withLoudnorm?: boolean } = {}) =>
  [
    "afftdn=nf=-25",
    "highpass=f=80",
    "lowpass=f=12000",
    "equalizer=f=200:t=q:w=1:g=-2",
    "equalizer=f=3000:t=q:w=1.5:g=3",
    "acompressor=threshold=-18dB:ratio=3:attack=5:release=120:makeup=3",
    "alimiter=limit=-1.5dB",
    ...(withLoudnorm ? [buildLoudnorm({ targetLufs, truePeakDb })] : []),
  ].join(",");

export interface MusicBedGraph {
  /** Index of the ffmpeg input carrying the bed (the source is input 0). */
  inputIndex: number;
  /** Bed level relative to the voice before loudnorm; the kit sits beds 16–20 LU under. */
  gainDb: number;
  /** Output length, for the fade-out. */
  durationMs: number;
  loudnorm: string;
}

/**
 * Music bed of the playbook (etap C2): EQ carves space for the voice (low mud and the
 * 2.5 kHz presence band), the bed ducks under speech through a sidechain compressor keyed
 * by the voice, both are mixed and only then normalised, so the bed never pushes the mix.
 */
export const buildMusicBedChains = (bed: MusicBedGraph): string[] => {
  const fadeStart = Math.max(0, bed.durationMs / 1000 - 1.5).toFixed(3);
  return [
    `[${bed.inputIndex}:a]highpass=f=40,equalizer=f=300:t=q:w=1:g=-3,equalizer=f=2500:t=q:w=2:g=-4,volume=${bed.gainDb}dB,afade=t=out:st=${fadeStart}:d=1.5[bed]`,
    `[bed][voicekey]sidechaincompress=threshold=0.05:ratio=8:attack=5:release=300[ducked]`,
    `[voice][ducked]amix=inputs=2:duration=first:dropout_transition=2:normalize=0,${bed.loudnorm}[amix]`,
  ];
};

export const buildClipFilterGraph = ({
  segments,
  inputOffsetMs = 0,
  targetWidth = 1080,
  targetHeight = 1920,
  framing = null,
  voiceChain = null,
  musicBed = null,
  endFadeMs = END_FADE_MS,
}: {
  segments: RenderSegment[];
  inputOffsetMs?: number;
  targetWidth?: number;
  targetHeight?: number;
  /** Face-tracked crop plan (etap A); null or `pad` keeps the full picture with bars. */
  framing?: FramingPlan | null;
  /** Audio filter string applied after concat (etap C1); null = the source audio untouched. */
  voiceChain?: string | null;
  /** Music bed mixed under the voice (etap C2); pass `voiceChain` built with `withLoudnorm: false`. */
  musicBed?: MusicBedGraph | null;
  /** Fade-out over the trailing silence; 0 = hard cut (nothing after the last word). */
  endFadeMs?: number;
}): ClipFilterGraph => {
  const chains: string[] = [];
  const concatInputs: string[] = [];
  const padChain = `scale=${targetWidth}:${targetHeight}:force_original_aspect_ratio=decrease:force_divisible_by=2:flags=lanczos,pad=${targetWidth}:${targetHeight}:(ow-iw)/2:(oh-ih)/2`;

  segments.forEach((segment, index) => {
    const from = msToSeconds(segment.fromMs - inputOffsetMs);
    const to = msToSeconds(segment.toMs - inputOffsetMs);
    const cut = `[0:v]trim=start=${from}:end=${to},setpts=PTS-STARTPTS`;
    const planned = framing && framing.mode !== "pad" ? framing.segments[index] : undefined;
    const crop = (track: FramingTrack, height: number) =>
      `crop=w=${framing!.crop.w}:h=${framing!.crop.h}:x=${buildCropXExpression(track, segment.fromMs)}:y=${track.y},scale=${targetWidth}:${height}:flags=lanczos`;

    if (planned && framing!.mode === "single" && planned.tracks[0]) {
      chains.push(`${cut},${crop(planned.tracks[0], targetHeight)},setsar=1[v${index}]`);
    } else if (planned && framing!.mode === "split" && planned.tracks.length === 2) {
      const half = targetHeight / 2;
      chains.push(
        `${cut},split=2[s${index}a][s${index}b]`,
        `[s${index}a]${crop(planned.tracks[0]!, half)}[t${index}a]`,
        `[s${index}b]${crop(planned.tracks[1]!, half)}[t${index}b]`,
        `[t${index}a][t${index}b]vstack=inputs=2,setsar=1[v${index}]`,
      );
    } else {
      chains.push(`${cut},${padChain},setsar=1[v${index}]`);
    }
    chains.push(`[0:a]atrim=start=${from}:end=${to},asetpts=PTS-STARTPTS[a${index}]`);
    concatInputs.push(`[v${index}][a${index}]`);
  });

  chains.push(`${concatInputs.join("")}concat=n=${segments.length}:v=1:a=1[vcat][acat]`);
  chains.push(`[vcat]format=yuv420p,setparams=color_primaries=bt709:color_trc=bt709:colorspace=bt709[vout]`);
  if (voiceChain && musicBed) {
    chains.push(`[acat]${voiceChain},asplit=2[voice][voicekey]`, ...buildMusicBedChains(musicBed));
  } else {
    chains.push(voiceChain ? `[acat]${voiceChain}[amix]` : "[acat]anull[amix]");
  }
  // The fade covers only the silence kept after the last word (see `trailingSilenceMs`), so
  // the ending sounds intended and a next-word onset that slipped into the tail is gone.
  const fadeMs = Math.round(Math.max(0, endFadeMs));
  if (fadeMs > 0) {
    const fadeStart = Math.max(0, (totalOutputDurationMs(segments) - fadeMs) / 1000).toFixed(3);
    chains.push(`[amix]afade=t=out:st=${fadeStart}:d=${(fadeMs / 1000).toFixed(3)}[aout]`);
  } else {
    chains.push("[amix]anull[aout]");
  }

  return { filterComplex: chains.join(";"), videoLabel: "[vout]", audioLabel: "[aout]" };
};
